import { useState } from 'react';
import { all, DocumentClass } from '@/api/doc/document-class';
import { add, DocumentType, DocumentTypeSchema } from '@/api/doc/document-type';
import { Button } from '@/components/ui/button';
import {
	Form,
	FormControl,
	FormDescription,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select';
import {
	Sheet,
	SheetContent,
	SheetHeader,
	SheetTitle,
	SheetTrigger,
} from '@/components/ui/sheet';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CirclePlus } from 'lucide-react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { toast } from 'sonner';

export function AddDocumentType() {
	const { data } = useQuery({
		queryFn: () => all({ includedExpired: false }),
		queryKey: ['document-classes'],
	});

	const [isSheetOpen, setIsSheetOpen] = useState(false);
	const queryClient = useQueryClient();

	const form = useForm<DocumentType>({
		resolver: zodResolver(DocumentTypeSchema),
		defaultValues: {
			id: null,
			name: '',
			slug: '',
			deletedOn: null,
			documentClass: {
				id: '',
				name: '---',
			},
		},
		mode: 'all',
	});

	const { mutate } = useMutation({
		mutationFn: (updatedDocument: DocumentType) => add(updatedDocument),
		onSuccess: () => {
			toast.success(`Success! The document type has been successfully added`, {
				dismissible: true,
			});
			setIsSheetOpen(false);
			queryClient.invalidateQueries({ queryKey: ['document-types'] });
		},
		onError: () => {
			toast.error(`Error! The document type could not be added`, {
				dismissible: true,
			});
		},
	});

	const onSubmit: SubmitHandler<DocumentType> = (values: DocumentType) =>
		mutate(values);

	form.watch();

	return (
		<Sheet open={isSheetOpen} onOpenChange={setIsSheetOpen}>
			<SheetTrigger asChild>
				<Button>
					<CirclePlus />
					Add Document Type
				</Button>
			</SheetTrigger>
			<SheetContent className="overflow-scroll">
				<SheetHeader>
					<SheetTitle>Add Document Type</SheetTitle>
				</SheetHeader>
				<Form {...form}>
					<form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
						<FormField
							control={form.control}
							name="name"
							render={({ field }) => (
								<FormItem>
									<FormLabel>Document Type Name</FormLabel>
									<FormControl>
										<Input {...field} placeholder="Document type name" />
									</FormControl>
									<FormDescription>Name the document type</FormDescription>
									<FormMessage />
								</FormItem>
							)}
						/>
						<FormField
							control={form.control}
							name="documentClass.id"
							render={({ field }) => (
								<FormItem>
									<FormLabel>Document Class</FormLabel>
									<Select onValueChange={field.onChange} value={field.value ?? ''}>
										<FormControl>
											<SelectTrigger>
												<SelectValue placeholder="Please select a document class." />
											</SelectTrigger>
										</FormControl>
										<SelectContent>
											{data!.map((documentClass: DocumentClass) => (
												<SelectItem key={documentClass.id} value={String(documentClass.id)}>
													{documentClass.name}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
									<FormDescription>
										Select a document class to associate with this document type.
									</FormDescription>
									<FormMessage />
								</FormItem>
							)}
						/>
						<Button
							type="submit"
							disabled={!form.formState.isValid || form.formState.isSubmitting}
						>
							{form.formState.isSubmitting ? 'Adding...' : 'Add'}
						</Button>
					</form>
				</Form>
			</SheetContent>
		</Sheet>
	);
}
